
import DateUtil from '@tekvue/assets/utils/DateUtil';
import Util from '@tekvue/assets/utils/Util';
import TkDate from '@tekvue/components/fields/TkDate.vue';
import { Component, Emit, Vue, Watch } from 'vue-property-decorator'
import { Filter, OptionHora } from '../types-mesa';
import SnackbarUtils from '@tekvue/assets/utils/SnackbarUtils';

@Component({
  name: 'FiltroMesa',
  components: {
    TkDate,
  },
})
export default class FiltroMesa extends Vue {
  // #region [ EVENTS ]
  @Emit('filters-applied')
  emitFiltersApplied(): Filter {
    this.emitDescriptionFiltersApplied();
    return Util.deepCopy(this.filters);
  }

  @Emit('description-filters-applied')
  emitDescriptionFiltersApplied(): string {
    return this.descriptionFilters;
  }

  currentDate = DateUtil.getCurrentDate();

  aberto = true;
  menuData = false;
  dialogHoras = false;
  iniciandoSelecao = false;
  filterApplied = false;

  filters: Filter = {
    diaCorrente: true,
    data: this.currentDate,
    horas: 'AUTO',
    quantidadeHoras: 2,
    horaInicial: '',
    horaFinal: '',
    horasSelecionadas: [] as string[],
    workStatus: 'TODOS',
  };

  /** Default filters for when the user first opens the page or resets the filters. */
  private readonly filtroDefault = Util.deepCopy(this.filters);

  /** Used to verify if the user changed the filters since they were last applied. */
  backup = JSON.stringify(this.filters);

  readonly fixedFiltersKeys = [
    'diaCorrente',
    'data',
    'horas',
  ] as const;

  readonly specificFiltersKeys: Record<OptionHora, (keyof Filter)[]> = {
    AUTO: ['quantidadeHoras'],
    DIA_INTEIRO: [],
    INTERVALO: ['horaInicial', 'horaFinal'],
    PERSONALIZADO: ['horasSelecionadas'],
  };

  readonly horaLista = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));

  horasOptions = [
    { text: 'Auto', value: 'AUTO' },
    { text: 'Dia inteiro', value: 'DIA_INTEIRO' },
    { text: 'Intervalo', value: 'INTERVALO' },
    { text: 'Personalizado', value: 'PERSONALIZADO' },
  ];

  get erroHoraFinal(): boolean {
    return !!(
      this.filters.horas === 'INTERVALO' &&
      this.filters.horaInicial &&
      this.filters.horaFinal &&
      parseInt(this.filters.horaFinal) < parseInt(this.filters.horaInicial)
    );
  }

  get erroHoras(): boolean {
    return this.filters.horas === 'AUTO' && !this.filters.diaCorrente;
  }

  get erroQuantidadeHoras(): boolean {
    return this.filters.quantidadeHoras > 23 || this.filters.quantidadeHoras < 0;
  }

  get canApplyFilters(): boolean {
    if (this.filters.horas === 'INTERVALO') {
      return (
        this.filters.horaInicial &&
        this.filters.horaFinal &&
        !this.erroHoraFinal
      );
    } else if (this.filters.horas === 'PERSONALIZADO') {
      return this.filters.horasSelecionadas.length > 0;
    } else if (this.filters.horas === 'AUTO') {
      return !this.erroHoras && !this.erroQuantidadeHoras;
    }
    return true;
  }

  /** Only the filters specific to the option selected. */
  get specificFilters(): Filter {
    const currentFilterFields = this.specificFiltersKeys[this.filters.horas];

    const filtersKeys = [
      ...this.fixedFiltersKeys,
      ...currentFilterFields,
    ];

    const currentFilters = Object.keys(this.filters)
      .filter((key) => [...filtersKeys].includes(key as keyof Filter))
      .reduce((obj, key) => {
        obj[key] = this.filters[key];
        return obj;
      }, {} as Filter);

    return currentFilters;
  }

  get descriptionFilters(): string {
    const filtersDescriptions: Partial<Record<keyof Filter, string>> = {
      data: this.filters.data.toString(),
      horas: `Horas: ${this.filters.horas}`,
      quantidadeHoras: `Quantidade de horas: ${this.filters.quantidadeHoras}`,
      horaInicial: `Hora inicial: ${this.filters.horaInicial}`,
      horaFinal: `Hora final: ${this.filters.horaFinal}`,
      horasSelecionadas: `Horas selecionadas: ${this.filters.horasSelecionadas.join(', ')}`,
    };

    return Object.keys(filtersDescriptions)
      .filter((key) => key in this.specificFilters)
      .map((key) => filtersDescriptions[key as keyof Filter])
      .join('  -  ');
  }

  created(): void {
    this.startCurrentDateUpdater();
  }

  mounted(): void {
    // Add global mouseup and blur event listeners
    window.addEventListener('mouseup', this.handleGlobalMouseUp);
    window.addEventListener('blur', this.handleGlobalMouseUp);
  }

  beforeDestroy(): void {
    // Remove global mouseup and blur event listeners
    window.removeEventListener('mouseup', this.handleGlobalMouseUp);
    window.removeEventListener('blur', this.handleGlobalMouseUp);
  }

  handleGlobalMouseUp(): void {
    this.iniciandoSelecao = false;
  }

  toggleHora(hora: string): void {
    const index = this.filters.horasSelecionadas.indexOf(hora);

    if (index === -1) {
      this.filters.horasSelecionadas.push(hora);
    } else {
      this.filters.horasSelecionadas.splice(index, 1);
    }

    this.filters.horasSelecionadas.sort();
  }

  get filtrosAlterados(): boolean {
    return JSON.stringify(this.filters) !== this.backup;
  }

  applyFilters(): void {
    const estrutura: string = this.$store.getters.estruturaGerencialAtual ? this.$store.getters.estruturaGerencialAtual.NRESTRUTURAM : null;

    if (estrutura === "-1") {
      SnackbarUtils.showMessage('Não é possível utilizar a estrutura "-- Todas --".', SnackbarUtils.TYPE_ALERT);
      return;
    }

    if (estrutura === null) {
      SnackbarUtils.showMessage('É necessário escolher uma estrutura gerencial no filtro superior.', SnackbarUtils.TYPE_ALERT);
      return;
    }

    this.backup = JSON.stringify(this.filters);
    this.emitFiltersApplied();
    this.closeFilter();
    this.filterApplied = true;
  }

  closeFilter(): void {
    this.aberto = false;
  }

  openFilter(): void {
    this.aberto = true;
  }

  resetFilter(): void {
    this.filters = {
      ...Util.deepCopy(this.filtroDefault),
      data: this.currentDate
    };
  }

  selectAllHours(): void {
    this.filters.horasSelecionadas = [...this.horaLista];
  }

  unselectAllHours(): void {
    this.filters.horasSelecionadas = [];
  }

  updateCurrentDate(): void {
    this.currentDate = DateUtil.getCurrentDate();

    if (this.filters.diaCorrente) {
      this.filters.data = this.currentDate;
      if (this.filterApplied) {
        this.emitDescriptionFiltersApplied();
      }
    }
  }

  startCurrentDateUpdater(): void {
    this.updateCurrentDate(); // Will update on the next day

    setTimeout(() => {
      setInterval(() => {
        this.updateCurrentDate(); // Updates every day
      }, 1000 * 60 * 60 * 24);
    }, DateUtil.getMsUntilNextDay());
  }
}
